import React from 'react';
import {observer} from 'mobx-react';
import {withState, t} from 'shared/core';
import {endpoints, types} from 'main/tools';
import {Layout, Panel} from 'shared/components';
import {InteractiveTable} from 'main/components';
import {PerformanceReviewReviewee} from 'main/stores/performance_reviews';
import ReviewInProgressSummaryPanel from './ReviewInProgressSummaryPanel';
import ReviewEndedSummaryPanel from './ReviewEndedSummaryPanel';
import {AdminContainerState} from '../state';
import {SelectReviewersModal} from 'main/containers/performance_reviews/components/reviews/components/manage/components/reviewees/components/select_reviewers';
import {AddRevieweeModal} from 'main/containers/performance_reviews/components/reviews/components/manage/components/reviewees/components//add_reviewee';
import {MANAGER_REVIEW_COLUMNS, NON_MANAGER_REVIEW_COLUMNS} from './columns';
import useModal from 'shared/hooks/useModal';
import EditDatesModal from 'main/containers/performance_reviews/components/reviews/components/view/components/EditDatesModal';
import ManageSharingModal from '../../ManageSharingModal';

const LOCALIZATION = {
  interactive: {
    addModel: 'performance_reviews.view.Add Participant'
  },
  removeModal: {
    header: 'performance_reviews.view.Remove Participant',
    subHeader: 'performance_reviews.view.Are you sure you want to remove this participant from the performance review?',
    body: 'performance_reviews.view.All reviews for this employee will be permanently deleted.'
  },
  editModel: 'performance_reviews.view.Edit Reviewers'
};

const ReviewSummaryPanel = ({uiState}) => {
  const {cycle} = uiState;

  if (cycle.state === 'ended') {
    return <ReviewEndedSummaryPanel uiState={uiState}/>;
  } else {
    return <ReviewInProgressSummaryPanel uiState={uiState}/>;
  }
};

const AdminContainer = observer(({uiState}) => {
  const manageSharingModalOpen = useModal(uiState, 'ManageSharingModal', 'selectedReviewee');
  const editDatesModalOpen = useModal(uiState, 'EditDatesModal', 'selectedReviewee', PerformanceReviewReviewee);
  const {cycle, match, selectedReviewee} = uiState;

  return (
    <Layout>
      <Panel.Stack loose>
        <ReviewSummaryPanel uiState={uiState}/>
        <InteractiveTable
          title={t('performance_reviews.view.Reviews')}
          uiState={uiState}
          backPath='admin'
          match={match}
          columns={cycle.managerType ? MANAGER_REVIEW_COLUMNS : NON_MANAGER_REVIEW_COLUMNS}
          localization={LOCALIZATION}
          onMount={agent => uiState.setInteractiveAgent(agent)}
          onEdit={(model) => uiState.openReviewerSelectionModal(model)}
          onRemove={(model) => uiState.removeReviewee(model)}
          showAddButton={!(cycle.state === 'ended')}
          onAdd={() => uiState.openAddRevieweeModal()}
          customLinks={(model) => uiState.customLinksFor(model)}
          horizontalScroll
          proxy={{
            type: 'async',
            modelType: types.PERFORMANCE_REVIEWS.REVIEWEE,
            model: PerformanceReviewReviewee,
            endpoint: endpoints.PERFORMANCE_REVIEWS.REVIEWEES.ADMIN.with(match.params.cycleId)
          }}
        />
        <SelectReviewersModal
          cycle={cycle}
          reviewee={selectedReviewee}
          isOpen={uiState.reviewerSelectionModalOpen}
          onHide={() => uiState.closeReviewerSelectionModal()}
          onSave={() => uiState.onReviewersSelected()}
        />
        <AddRevieweeModal
          cycle={cycle}
          isOpen={uiState.addRevieweeModalOpen}
          onHide={() => uiState.closeAddRevieweeModal()}
          onSave={() => uiState.onRevieweeAdded()}
        />
        <ManageSharingModal
          isOpen={manageSharingModalOpen}
          onClose={() => uiState.closeManageSharingModal()}
          reviewee={selectedReviewee}
          onShare={(review) => uiState.shareReview(review)}
          onUnshare={(review) => uiState.unshareReview(review)}
        />
        <EditDatesModal uiState={uiState} isOpen={editDatesModalOpen}/>
      </Panel.Stack>
    </Layout>
  );
});

export default withState(AdminContainer, AdminContainerState);
